/* eslint-disable prefer-destructuring */
import React, { useContext, useEffect, useState } from 'react';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useParams, useHistory } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Loading } from '../../components/Loading';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { NotificationContext } from '../../components/GrowlNotifications';
import RenewSubscription from './RenewSubscription';
import ManagedAccounts from './ManagedAccounts';

const BUY_OR_REACTIVATE_CONFIG_QUERY = gql`
  query buyOrReactivateConfigQuery($accountId: ID!) {
    account(id: $accountId) {
      clientAccountsWithoutActiveSubscriptions {
        id
        displayName
        type
        status
        paidBy
        subscriptionInformation {
          currentSubscription {
            purchasedOn
            endsOn
            status
            numberOfEmployees
            duration
          }
        }
        parent {
          id
          displayName
          type
        }
      }
    }
  }
`;

const RENEW_SUBSCRIPTION_MUTATION = gql`
  mutation renewSubscriptionMutation($managingAccountId: ID!, $clientAccountId: ID!) {
    renewSubscription(managingAccountId: $managingAccountId, clientAccountId: $clientAccountId)
  }
`;

const i18nMessages = defineMessages({
  noLimit: {
    id: 'menu.noLimit',
    defaultMessage: 'NoLimit',
  },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  id: { id: 'attrs.id', defaultMessage: 'Id' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  organisation: {
    id: 'lbl.organisation',
    defaultMessage: 'Organisation',
  },
  renewSubscription: {
    id: 'business-center.renew-subscription',
    defaultMessage: 'Renew Subscription',
  },
  noSubscriptions: {
    id: 'business-center.no-subscriptions',
    defaultMessage: 'There are currently no account subscriptions to manage',
  },
});

const BuyOrReactivate = () => {
  const { formatMessage } = useIntl();
  const { aid: accountId } = useParams();
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [accounts, setAccounts] = useState([]);
  const [accountToRenew, setAccountToRenew] = useState(null);

  const { loading, error, data } = useQuery(BUY_OR_REACTIVATE_CONFIG_QUERY, {
    variables: {
      accountId,
    },
    fetchPolicy: 'no-cache',
  });
  const [renewSubscriptionMutation] = useMutation(RENEW_SUBSCRIPTION_MUTATION, {
    refetchQueries: ['buyOrReactivateConfigQuery'],
  });

  const { account } = data;

  useEffect(() => {
    if (account) {
      setAccounts(account.clientAccountsWithoutActiveSubscriptions);
    }
  }, [account]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const renewSubscription = async () => {
    try {
      await renewSubscriptionMutation({
        variables: {
          managingAccountId: accountId,
          clientAccountId: accountToRenew,
        },
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      setAccountToRenew(null);
    }
  };

  const redirectToBuySubscriptionPage = () => {
    history.push(`/accounts/${accountId}/buy-subscription-for-client?clientId=${accountToRenew}`);
  };

  const accountToRenewSubscriptionFor = accounts.filter(accountToFilter => accountToFilter.id === accountToRenew)[0];

  return (
    <>
      {accountToRenew && (
        <Dialog
          visible={accountToRenew}
          header={formatMessage(i18nMessages.renewSubscription)}
          onHide={() => {
            setAccountToRenew(null);
          }}
        >
          <RenewSubscription
            account={accountToRenewSubscriptionFor}
            renewSubscription={renewSubscription}
            redirectToBuySubscriptionPage={redirectToBuySubscriptionPage}
          />
        </Dialog>
      )}
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <div className="p-sm-12 p-md-12 p-lg-12">
              <Panel header={formatMessage(i18nMessages.noLimit)}>
                <ManagedAccounts
                  accounts={accounts}
                  handleRowClick={accountIdToRenew => setAccountToRenew(accountIdToRenew)}
                />
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BuyOrReactivate;
